"use client";

import { useMemo, useState } from "react";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";

type SortOrder = "frequency" | "alphabetical";

const LETTER_RE = /[a-zA-Z]/;

interface LetterRow {
  letter: string;
  count: number;
  percentage: number;
}

function countLetters(
  text: string,
  caseSensitive: boolean
): Map<string, number> {
  const counts = new Map<string, number>();
  for (const ch of text) {
    if (!LETTER_RE.test(ch)) {
      continue;
    }
    const key = caseSensitive ? ch : ch.toLowerCase();
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  return counts;
}

function buildRows(counts: Map<string, number>, order: SortOrder): LetterRow[] {
  let totalLetters = 0;
  for (const count of counts.values()) {
    totalLetters += count;
  }
  const rows: LetterRow[] = [];
  for (const [letter, count] of counts.entries()) {
    const percentage = totalLetters === 0 ? 0 : (count / totalLetters) * 100;
    rows.push({ letter, count, percentage });
  }
  if (order === "alphabetical") {
    rows.sort((a, b) => a.letter.localeCompare(b.letter));
  } else {
    rows.sort((a, b) => b.count - a.count || a.letter.localeCompare(b.letter));
  }
  return rows;
}

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 text-center">
      <p className="font-semibold text-2xl text-foreground">{value}</p>
      <p className="mt-1 text-muted-foreground text-sm">{label}</p>
    </div>
  );
}

/** English strings. A translated content file overrides these through `ui`. */
export const LETTER_FREQUENCY_COUNTER_STRINGS = {
  options: "Options",
  sortByFrequency: "Sort by frequency",
  sortAlphabetically: "Sort alphabetically",
  caseSensitive: "Case-sensitive",
  yourText: "Your text",
  inputPlaceholder: "Paste or type your text here…",
  letters: "Letters",
  characters: "Characters",
  distinctLetters: "Distinct letters",
  letterFrequency: "Letter frequency",
  letter: "Letter",
  count: "Count",
  percentage: "Percentage",
  frequency: "Frequency",
} as const;

export default function LetterFrequencyCounterWidget() {
  const [input, setInput] = useState("");
  const [order, setOrder] = useState<SortOrder>("frequency");
  const [caseSensitive, setCaseSensitive] = useState(false);

  const strings = useToolUi(LETTER_FREQUENCY_COUNTER_STRINGS);
  const fmt = useToolNumber();

  const rows = useMemo(
    () => buildRows(countLetters(input, caseSensitive), order),
    [input, caseSensitive, order]
  );
  const totalLetters = useMemo(() => {
    let sum = 0;
    for (const row of rows) {
      sum += row.count;
    }
    return sum;
  }, [rows]);
  const maxCount = rows.length > 0 ? Math.max(...rows.map((r) => r.count)) : 0;

  return (
    <>
      {/* Options */}
      <section className="space-y-3">
        <p className="font-medium text-foreground text-sm">{strings.options}</p>
        <div className="flex flex-wrap gap-3">
          <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5">
            <input
              checked={order === "frequency"}
              className="accent-primary"
              name="letter-sort-order"
              onChange={() => setOrder("frequency")}
              type="radio"
              value="frequency"
            />
            {strings.sortByFrequency}
          </label>
          <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5">
            <input
              checked={order === "alphabetical"}
              className="accent-primary"
              name="letter-sort-order"
              onChange={() => setOrder("alphabetical")}
              type="radio"
              value="alphabetical"
            />
            {strings.sortAlphabetically}
          </label>
          <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5">
            <input
              checked={caseSensitive}
              className="accent-primary"
              onChange={(e) => setCaseSensitive(e.target.checked)}
              type="checkbox"
            />
            {strings.caseSensitive}
          </label>
        </div>
      </section>

      {/* Input */}
      <div className="space-y-2">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="letter-input"
        >
          {strings.yourText}
        </label>
        <textarea
          className="min-h-[200px] w-full resize-y rounded-lg border border-border bg-card p-4 font-mono text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          id="letter-input"
          onChange={(e) => setInput(e.target.value)}
          placeholder={strings.inputPlaceholder}
          value={input}
        />
      </div>

      {/* Totals */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
        <StatCard label={strings.letters} value={fmt(totalLetters)} />
        <StatCard label={strings.characters} value={fmt(input.length)} />
        <StatCard label={strings.distinctLetters} value={rows.length} />
      </div>

      {/* Frequency table */}
      {rows.length > 0 && (
        <section className="space-y-3">
          <h2 className="font-semibold text-foreground text-xl">
            {strings.letterFrequency}
          </h2>
          <div className="overflow-x-auto rounded-lg border border-border bg-card">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-border border-b">
                  <th className="p-3 font-medium text-foreground">
                    {strings.letter}
                  </th>
                  <th className="p-3 font-medium text-foreground">
                    {strings.count}
                  </th>
                  <th className="p-3 font-medium text-foreground">
                    {strings.percentage}
                  </th>
                  <th className="w-1/2 p-3 font-medium text-foreground">
                    {strings.frequency}
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr
                    className="border-border border-b last:border-b-0"
                    key={row.letter}
                  >
                    <td className="p-3 font-mono font-semibold text-foreground">
                      {row.letter}
                    </td>
                    <td className="p-3 text-muted-foreground">
                      {fmt(row.count)}
                    </td>
                    <td className="p-3 text-muted-foreground">
                      {fmt(row.percentage, {
                        minimumFractionDigits: 1,
                        maximumFractionDigits: 1,
                      })}
                      %
                    </td>
                    <td className="p-3">
                      <div className="h-3 w-full rounded bg-muted">
                        <div
                          className="h-3 rounded bg-primary"
                          style={{
                            width: `${maxCount === 0 ? 0 : (row.count / maxCount) * 100}%`,
                          }}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </>
  );
}
